import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  FieldBlock,
  Input,
  LoadingState,
  Select,
  TagSelect,
  useToast,
} from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { CANAIS_LIBERADOS, PERMISSION_CATALOG, temAlgumCanal, type CanaisDeAcesso } from '@orbind/core'
import { CamposDeCanais } from '../../components/CamposDeCanais.tsx'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import { MODULES } from '../../shell/modules.ts'

/**
 * Controle de acesso (SPEC-20260814-1444) — tres secoes REAIS do admin:
 * Perfis (matriz de permissoes), Grupos (membros + perfis) e Membros
 * (cadastro real + perfis diretos). Tudo no workspace da SESSAO.
 */

type ProfileList = Awaited<ReturnType<Awaited<ReturnType<(typeof api)['access-control']['profiles']['$get']>>['json']>>
type ProfileRow = ProfileList['profiles'][number]
type GroupList = Awaited<ReturnType<Awaited<ReturnType<(typeof api)['access-control']['groups']['$get']>>['json']>>
type GroupRow = GroupList['groups'][number]
type CollabList = Awaited<
  ReturnType<Awaited<ReturnType<(typeof api)['access-control']['members']['$get']>>['json']>
>
type CollabRow = CollabList['members'][number]

/** Rotulo do modulo no catalogo de permissoes (nome do rail quando houver). */
function rotuloDoModulo(moduleId: string, fallback: string): string {
  return MODULES.find((m) => m.id === moduleId)?.fallbackLabel ?? (moduleId === 'admin' ? 'Administração' : fallback)
}

function useAoFalhar() {
  const toast = useToast()
  return (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })
}

/** Toda mutacao de acesso muda o que usuarios veem: invalida os bundles. */
function useInvalidarAcesso() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['access-profiles'] })
    void qc.invalidateQueries({ queryKey: ['access-groups'] })
    void qc.invalidateQueries({ queryKey: ['access-members'] })
    void qc.invalidateQueries({ queryKey: ['access-control-bundle'] })
  }
}

const cabecalho = (titulo: string, descricao: string, acao?: React.ReactNode) => (
  <div className="adm-head">
    <div>
      <h1 className="adm-head__title">{titulo}</h1>
      <p className="adm-head__desc">{descricao}</p>
    </div>
    {acao}
  </div>
)

// ==================================================================== Perfis

type FormPerfil = { name: string; description: string; permissionKeys: readonly string[] }
const PERFIL_VAZIO: FormPerfil = { name: '', description: '', permissionKeys: [] }

export function AccessProfilesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const aoFalhar = useAoFalhar()
  const invalidar = useInvalidarAcesso()

  const lista = useQuery({
    queryKey: ['access-profiles'],
    queryFn: async () => (await api['access-control'].profiles.$get()).json(),
  })

  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormPerfil>(PERFIL_VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) {
      setForm(PERFIL_VAZIO)
      setEditando({ id: null })
    }
  }, [criarSinal])

  const salvarPermissoes = async (id: string, keys: readonly string[]) => {
    await api['access-control'].profiles[':id'].permissions.$put({
      param: { id },
      json: { permissionKeys: [...keys] },
    })
  }

  const criar = useMutation({
    mutationFn: async (f: FormPerfil) => {
      const resposta = await (
        await api['access-control'].profiles.$post({
          json: { name: f.name.trim(), description: f.description.trim() || null },
        })
      ).json()
      await salvarPermissoes(resposta.profile.id, f.permissionKeys)
    },
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Perfil criado.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f, isSystem }: { id: string; f: FormPerfil; isSystem: boolean }) => {
      await api['access-control'].profiles[':id'].$patch({
        param: { id },
        json: { name: f.name.trim(), description: f.description.trim() || null },
      })
      if (!isSystem) await salvarPermissoes(id, f.permissionKeys)
    },
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Perfil atualizado.' })
    },
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) =>
      (await api['access-control'].profiles[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Perfil excluído.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const profiles = lista.data?.profiles ?? []
  const emEdicao = editando?.id ? profiles.find((p) => p.id === editando.id) : undefined

  const abrir = (alvo: ProfileRow | null) => {
    setForm(
      alvo
        ? { name: alvo.name, description: alvo.description ?? '', permissionKeys: alvo.permissionKeys }
        : PERFIL_VAZIO,
    )
    setEditando({ id: alvo?.id ?? null })
  }

  const alternarChaves = (chaves: readonly string[], ligar: boolean) => {
    setForm((f) => {
      const atual = new Set(f.permissionKeys)
      for (const k of chaves) {
        if (ligar) atual.add(k)
        else atual.delete(k)
      }
      return { ...f, permissionKeys: [...atual] }
    })
  }

  /** Dirty check: compara nome, descricao e o conjunto de permissoes. */
  const normalizado = (f: FormPerfil) => ({
    name: f.name.trim(),
    description: f.description.trim(),
    permissionKeys: [...f.permissionKeys].sort(),
  })
  const alterado =
    editando?.id == null
      ? form.name.trim().length >= 2
      : emEdicao !== undefined &&
        JSON.stringify(normalizado(form)) !==
          JSON.stringify(
            normalizado({
              name: emEdicao.name,
              description: emEdicao.description ?? '',
              permissionKeys: emEdicao.permissionKeys,
            }),
          )

  // ------------------------------------------- formulário (FormScreen) ---
  if (editando !== null) {
    const salvarValido = alterado && form.name.trim().length >= 2 && !criar.isPending && !atualizar.isPending
    return (
      <FormScreen
        title={editando.id ? 'Editar perfil' : 'Novo perfil'}
        subtitle={editando.id ? emEdicao?.name : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar perfil'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form, isSystem: Boolean(emEdicao?.isSystem) })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações do perfil">
          <FormRow cols="1fr 2fr">
            <FieldBlock label="Nome do perfil" required>
              {({ id, invalid }) => (
                <Input id={id} invalid={invalid} autoFocus value={form.name} disabled={Boolean(emEdicao?.isSystem)}
                  placeholder="Ex.: Gestor, Operação, Somente leitura"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              )}
            </FieldBlock>
            <FieldBlock label="Descrição">
              {({ id }) => (
                <Input id={id} value={form.description} placeholder="Para quem é este perfil"
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        <FormSection title="Permissões">
          {emEdicao?.isSystem ? (
            <Alert tone="info">O perfil Administrador concede todas as permissões — a matriz não se aplica.</Alert>
          ) : (
            <>
              <p className="adm-hint">
                Acesso ao módulo mostra o item no menu; as ações liberam os botões. Sem a ação, a API bloqueia mesmo
                que a tela apareça.
              </p>
              <div className="adm-permgrid" role="group" aria-label="Matriz de permissões">
                {PERMISSION_CATALOG.map((mod) => (
                  <div key={mod.moduleId} className="adm-permgrid__mod">
                    <Checkbox
                      label={`${rotuloDoModulo(mod.moduleId, mod.label)} — acesso ao módulo`}
                      checked={form.permissionKeys.includes(mod.accessKey)}
                      onChange={(e) => alternarChaves([mod.accessKey], e.target.checked)}
                    />
                    {mod.features.map((feat) => (
                      <div key={feat.key} style={{ paddingLeft: 24 }} className="ob-stack">
                        {feat.actions.map((acao) => (
                          <Checkbox
                            key={acao.key}
                            label={`${feat.label}: ${acao.label}`}
                            checked={form.permissionKeys.includes(acao.key)}
                            onChange={(e) => alternarChaves([acao.key], e.target.checked)}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </FormSection>
      </FormScreen>
    )
  }

  const topo = cabecalho(
    'Perfis de acesso',
    'O que cada perfil pode: módulos, funcionalidades e ações. Usuários recebem perfis direto ou por grupos; o efetivo é a união.',
    <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
      Incluir
    </Button>,
  )

  if (lista.isPending) return (<>{topo}<LoadingState variant="list" label="Carregando os perfis" /></>)
  if (lista.isError) {
    return (
      <>
        {topo}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar os perfis"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {topo}
      {profiles.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-id-badge" title="Nenhum perfil ainda"
            description="Crie perfis como “Gestor” ou “Operação” marcando o que cada um pode fazer."
            action={<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Novo perfil</Button>} />
        </div>
      ) : (
        <div className="adm-list">
          {profiles.map((row) => {
            const legenda = [
              row.description,
              row.isSystem ? 'Concede todas as permissões' : `${row.permissionKeys.length} ${row.permissionKeys.length === 1 ? 'permissão' : 'permissões'}`,
              `${row.memberIds.length} ${row.memberIds.length === 1 ? 'usuário' : 'usuários'}`,
              `${row.groupCount} ${row.groupCount === 1 ? 'grupo' : 'grupos'}`,
            ].filter(Boolean).join(' · ')
            const confirmando = confirmandoExclusao === row.id
            return (
              <div key={row.id} className="adm-catrow">
                <span className="adm-catrow__icon"><i className="fas fa-id-badge" aria-hidden /></span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.name}</span>
                  <span className="adm-catrow__sub">{legenda}</span>
                </span>
                {row.isSystem ? <span className="adm-sys">Sistema</span> : null}
                {confirmando ? (
                  // confirmacao INLINE — sem modal, como o usuario prefere
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>
                      Excluir “{row.name}”? Quem o tinha perde as permissões na hora.
                    </span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${row.name}`} title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger" disabled={row.isSystem}
                      aria-label={row.isSystem ? `${row.name} é do sistema e não pode ser excluído` : `Excluir ${row.name}`}
                      title={row.isSystem ? 'Perfil do sistema: não pode ser excluído' : 'Excluir'}
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

// ==================================================================== Grupos

type FormGrupo = { name: string; description: string; memberIds: readonly string[]; profileIds: readonly string[] }
const GRUPO_VAZIO: FormGrupo = { name: '', description: '', memberIds: [], profileIds: [] }

export function AccessGroupsSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const aoFalhar = useAoFalhar()
  const invalidar = useInvalidarAcesso()

  const lista = useQuery({
    queryKey: ['access-groups'],
    queryFn: async () => (await api['access-control'].groups.$get()).json(),
  })
  const perfis = useQuery({
    queryKey: ['access-profiles'],
    queryFn: async () => (await api['access-control'].profiles.$get()).json(),
  })
  const membros = useQuery({
    queryKey: ['access-members'],
    queryFn: async () => (await api['access-control'].members.$get()).json(),
  })

  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormGrupo>(GRUPO_VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) {
      setForm(GRUPO_VAZIO)
      setEditando({ id: null })
    }
  }, [criarSinal])

  const salvarVinculos = async (id: string, f: FormGrupo) => {
    await api['access-control'].groups[':id'].members.$put({ param: { id }, json: { memberIds: [...f.memberIds] } })
    await api['access-control'].groups[':id'].profiles.$put({ param: { id }, json: { profileIds: [...f.profileIds] } })
  }

  const criar = useMutation({
    mutationFn: async (f: FormGrupo) => {
      const resposta = await (
        await api['access-control'].groups.$post({ json: { name: f.name.trim(), description: f.description.trim() || null } })
      ).json()
      await salvarVinculos(resposta.group.id, f)
    },
    onSuccess: () => { invalidar(); setEditando(null); toast.push({ tone: 'success', message: 'Grupo criado.' }) },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormGrupo }) => {
      await api['access-control'].groups[':id'].$patch({ param: { id }, json: { name: f.name.trim(), description: f.description.trim() || null } })
      await salvarVinculos(id, f)
    },
    onSuccess: () => { invalidar(); setEditando(null); toast.push({ tone: 'success', message: 'Grupo atualizado.' }) },
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api['access-control'].groups[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => { invalidar(); setConfirmandoExclusao(null); toast.push({ tone: 'success', message: 'Grupo excluído.' }) },
    onError: (erro) => { setConfirmandoExclusao(null); aoFalhar(erro) },
  })

  const groups = lista.data?.groups ?? []
  const emEdicao = editando?.id ? groups.find((g) => g.id === editando.id) : undefined

  const abrir = (alvo: GroupRow | null) => {
    setForm(alvo
      ? { name: alvo.name, description: alvo.description ?? '', memberIds: alvo.memberIds, profileIds: alvo.profileIds }
      : GRUPO_VAZIO)
    setEditando({ id: alvo?.id ?? null })
  }

  /** Dirty check: nome, descricao e os conjuntos de membros/perfis. */
  const normalizado = (f: FormGrupo) => ({
    name: f.name.trim(),
    description: f.description.trim(),
    memberIds: [...f.memberIds].sort(),
    profileIds: [...f.profileIds].sort(),
  })
  const alterado =
    editando?.id == null
      ? form.name.trim().length >= 2
      : emEdicao !== undefined &&
        JSON.stringify(normalizado(form)) !==
          JSON.stringify(
            normalizado({
              name: emEdicao.name,
              description: emEdicao.description ?? '',
              memberIds: emEdicao.memberIds,
              profileIds: emEdicao.profileIds,
            }),
          )

  // ------------------------------------------- formulário (FormScreen) ---
  if (editando !== null) {
    const salvarValido = alterado && form.name.trim().length >= 2 && !criar.isPending && !atualizar.isPending
    return (
      <FormScreen
        title={editando.id ? 'Editar grupo' : 'Novo grupo'}
        subtitle={editando.id ? emEdicao?.name : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar grupo'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações do grupo">
          <FormRow cols="1fr 2fr">
            <FieldBlock label="Nome do grupo" required>
              {({ id, invalid }) => (
                <Input id={id} invalid={invalid} autoFocus value={form.name} placeholder="Ex.: Engenharia, Atendimento"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              )}
            </FieldBlock>
            <FieldBlock label="Descrição">
              {({ id }) => (
                <Input id={id} value={form.description} placeholder="Quem entra neste grupo"
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        <FormSection title={`Membros${form.memberIds.length > 0 ? ` (${form.memberIds.length})` : ''}`}>
          <div className="adm-permgrid" role="group" aria-label="Membros do grupo">
            {(membros.data?.members ?? []).map((cItem) => (
              <Checkbox key={cItem.memberId} label={cItem.name}
                checked={form.memberIds.includes(cItem.memberId)}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    memberIds: e.target.checked
                      ? [...f.memberIds, cItem.memberId]
                      : f.memberIds.filter((id) => id !== cItem.memberId),
                  }))
                } />
            ))}
          </div>
        </FormSection>

        <FormSection title={`Perfis concedidos${form.profileIds.length > 0 ? ` (${form.profileIds.length})` : ''}`}>
          <p className="adm-hint">Todos os membros do grupo herdam estes perfis, além dos perfis diretos de cada um.</p>
          <div className="adm-permgrid" role="group" aria-label="Perfis do grupo">
            {(perfis.data?.profiles ?? []).map((p) => (
              <Checkbox key={p.id} label={p.name}
                checked={form.profileIds.includes(p.id)}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    profileIds: e.target.checked ? [...f.profileIds, p.id] : f.profileIds.filter((id) => id !== p.id),
                  }))
                } />
            ))}
          </div>
        </FormSection>
      </FormScreen>
    )
  }

  const topo = cabecalho(
    'Grupos de acesso',
    'Grupos reúnem usuários e concedem perfis a todos de uma vez — ideal para times.',
    <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Incluir</Button>,
  )

  if (lista.isPending) return (<>{topo}<LoadingState variant="list" label="Carregando os grupos" /></>)
  if (lista.isError) {
    return (
      <>
        {topo}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar os grupos"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {topo}
      {groups.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-users-cog" title="Nenhum grupo ainda"
            description="Crie um grupo por time — ex.: “Engenharia” com o perfil Operação — e gerencie acesso em lote."
            action={<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Novo grupo</Button>} />
        </div>
      ) : (
        <div className="adm-list">
          {groups.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            return (
              <div key={row.id} className="adm-catrow">
                <span className="adm-catrow__icon"><i className="fas fa-users-cog" aria-hidden /></span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.name}</span>
                  <span className="adm-catrow__sub">
                    {[row.description, `${row.memberIds.length} ${row.memberIds.length === 1 ? 'membro' : 'membros'}`, `${row.profileIds.length} ${row.profileIds.length === 1 ? 'perfil' : 'perfis'}`].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {confirmando ? (
                  // confirmacao INLINE — sem modal, como o usuario prefere
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>
                      Excluir “{row.name}”? Os membros perdem os perfis concedidos por ele.
                    </span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${row.name}`} title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger" aria-label={`Excluir ${row.name}`} title="Excluir" onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

// ============================================================= Membros

type FormColab = { name: string; email: string; password: string; accessGroupId: string | null; workspaceIds: string[] }
const COLAB_VAZIO: FormColab = { name: '', email: '', password: '', accessGroupId: null, workspaceIds: [] }

type FormEditColab = {
  name: string
  email: string
  password: string
  isActive: boolean
  accessGroupIds: string[]
  workspaceIds: string[]
  /** Regra própria de canais neste workspace; nulo = segue o padrão (SPEC-20261008-1104-canais-de-acesso). */
  accessChannels: CanaisDeAcesso | null
}
const EDIT_VAZIO: FormEditColab = {
  name: '',
  email: '',
  password: '',
  isActive: true,
  accessGroupIds: [],
  workspaceIds: [],
  accessChannels: null,
}

export function AccessMembersSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const aoFalhar = useAoFalhar()
  const invalidar = useInvalidarAcesso()

  const lista = useQuery({
    queryKey: ['access-members'],
    queryFn: async () => (await api['access-control'].members.$get()).json(),
  })
  const perfis = useQuery({
    queryKey: ['access-profiles'],
    queryFn: async () => (await api['access-control'].profiles.$get()).json(),
  })
  const grupos = useQuery({
    queryKey: ['access-groups'],
    queryFn: async () => (await api['access-control'].groups.$get()).json(),
  })
  // workspaces da conta + qual e o da sessao (para pre-selecionar)
  const wsConta = useQuery({
    queryKey: ['account-workspaces'],
    queryFn: async () => (await api['access-control']['account-workspaces'].$get()).json(),
  })

  const [criando, setCriando] = useState(false)
  const [form, setForm] = useState<FormColab>(COLAB_VAZIO)
  const [perfisDe, setPerfisDe] = useState<CollabRow | null>(null)
  const [perfisSelecionados, setPerfisSelecionados] = useState<readonly string[]>([])
  // edicao dos DADOS do membro (2026-08-27)
  const [editando, setEditando] = useState<CollabRow | null>(null)
  const [formEdit, setFormEdit] = useState<FormEditColab>(EDIT_VAZIO)

  useEffect(() => {
    if (criarSinal > 0) {
      // ja abre com o workspace da sessao marcado (nao ha membro sem workspace)
      const atual = wsConta.data?.currentWorkspaceId
      setForm({ ...COLAB_VAZIO, workspaceIds: atual ? [atual] : [] })
      setCriando(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  const criar = useMutation({
    mutationFn: async (f: FormColab) =>
      (
        await api['access-control'].members.$post({
          json: {
            name: f.name.trim(),
            email: f.email.trim(),
            password: f.password || undefined,
            accessGroupId: f.accessGroupId ?? '',
            workspaceIds: f.workspaceIds,
          },
        })
      ).json(),
    onSuccess: (dados) => {
      invalidar()
      setCriando(false)
      toast.push({
        tone: 'success',
        message: dados.existed ? 'Membro já existia — vinculado ao workspace.' : 'Membro criado e vinculado.',
      })
    },
    onError: aoFalhar,
  })

  const salvarPerfis = useMutation({
    mutationFn: async ({ memberId, profileIds }: { memberId: string; profileIds: readonly string[] }) =>
      (
        await api['access-control'].members[':memberId'].profiles.$put({
          param: { memberId },
          json: { profileIds: [...profileIds] },
        })
      ).json(),
    onSuccess: () => {
      invalidar()
      setPerfisDe(null)
      toast.push({ tone: 'success', message: 'Perfis do membro atualizados.' })
    },
    onError: aoFalhar,
  })

  /** Editar os DADOS do membro (nome, e-mail, ativo, grupos, workspaces). */
  const salvarDados = useMutation({
    mutationFn: async ({ memberId, json }: { memberId: string; json: Parameters<(typeof api)['access-control']['members'][':memberId']['$patch']>[0]['json'] }) =>
      (await api['access-control'].members[':memberId'].$patch({ param: { memberId }, json })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Membro atualizado.' })
    },
    onError: aoFalhar,
  })

  const abrirEdicao = (row: CollabRow) => {
    setFormEdit({
      name: row.name,
      email: row.email,
      password: '',
      isActive: row.isActive,
      accessGroupIds: [...row.groupIds],
      workspaceIds: [...row.workspaceIds],
      accessChannels: row.accessChannels,
    })
    setEditando(row)
  }

  const members = lista.data?.members ?? []
  const nomeDoPerfil = (id: string) => perfis.data?.profiles.find((p) => p.id === id)?.name ?? '?'
  const nomeDoGrupo = (id: string) => grupos.data?.groups.find((g) => g.id === id)?.name ?? '?'

  // --------------------------------------- incluir (padrao FormScreen) ---
  if (criando) {
    const preenchido =
      form.name.trim() !== '' || form.email.trim() !== '' || form.password !== '' || form.accessGroupId !== null
    const salvarValido =
      form.name.trim().length >= 2 &&
      form.email.includes('@') &&
      form.accessGroupId !== null &&
      form.workspaceIds.length >= 1 &&
      !criar.isPending
    return (
      <FormScreen
        title="Novo membro"
        onClose={() => setCriando(false)}
        dirty={preenchido}
        dirtyHint="Preencha nome, e-mail, grupo de acesso e ao menos um workspace"
        saveLabel="Criar membro"
        saveDisabled={!salvarValido}
        saving={criar.isPending}
        onSubmit={() => salvarValido && criar.mutate(form)}
      >
        <FormSection title="Informações do membro">
          <FormRow cols="1fr 1fr 1fr">
            <FieldBlock label="Nome completo" required>
              {({ id }) => (
                <Input id={id} autoFocus value={form.name} placeholder="Ex.: Marina Coelho"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              )}
            </FieldBlock>
            <FieldBlock label="E-mail" required hint="Se o e-mail já existir na instalação, a pessoa só é vinculada.">
              {({ id }) => (
                <Input id={id} type="email" value={form.email} placeholder="nome@empresa.com.br"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              )}
            </FieldBlock>
            <FieldBlock label="Senha inicial" hint="Obrigatória para membro novo; a pessoa troca depois em “Esqueci minha senha”.">
              {({ id }) => (
                <Input id={id} type="password" value={form.password} placeholder="Mínimo 6 caracteres"
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        <FormSection title="Acesso">
          <FormRow cols="1fr 1fr">
            <FieldBlock
              label="Grupo de acesso"
              required
              hint="Define as permissões do membro. Sem grupo de acesso a pessoa não consegue entrar."
            >
              {({ id }) => (
                <Select
                  id={id}
                  value={form.accessGroupId}
                  placeholder={grupos.data?.groups.length ? 'Selecione um grupo' : 'Nenhum grupo cadastrado'}
                  options={(grupos.data?.groups ?? []).map((g) => ({ value: g.id, label: g.name }))}
                  onChange={(v) => setForm((f) => ({ ...f, accessGroupId: v }))}
                />
              )}
            </FieldBlock>
            <FieldBlock
              label="Workspaces"
              required
              hint="Em quais workspaces da conta o membro vai trabalhar. É obrigatório ao menos um."
            >
              {({ id }) => (
                <TagSelect
                  id={id}
                  value={form.workspaceIds}
                  placeholder="Selecione ao menos um"
                  emptyLabel="Nenhum workspace"
                  options={(wsConta.data?.workspaces ?? []).map((w) => ({ value: w.id, label: w.name }))}
                  onChange={(v) => setForm((f) => ({ ...f, workspaceIds: v }))}
                />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>
      </FormScreen>
    )
  }

  // -------------------------------- editar dados (padrao FormScreen) ---
  if (editando !== null) {
    const orig = editando
    const mudou =
      formEdit.name.trim() !== orig.name ||
      formEdit.email.trim() !== orig.email ||
      formEdit.isActive !== orig.isActive ||
      formEdit.password !== '' ||
      JSON.stringify([...formEdit.accessGroupIds].sort()) !== JSON.stringify([...orig.groupIds].sort()) ||
      JSON.stringify([...formEdit.workspaceIds].sort()) !== JSON.stringify([...orig.workspaceIds].sort()) ||
      JSON.stringify(formEdit.accessChannels) !== JSON.stringify(orig.accessChannels)
    const editValido =
      (formEdit.accessChannels === null || temAlgumCanal(formEdit.accessChannels)) &&
      formEdit.name.trim().length >= 2 &&
      formEdit.email.includes('@') &&
      formEdit.accessGroupIds.length >= 1 &&
      formEdit.workspaceIds.length >= 1 &&
      !salvarDados.isPending
    const ehDono = orig.role === 'owner'
    return (
      <FormScreen
        title="Editar membro"
        subtitle={orig.name}
        onClose={() => setEditando(null)}
        dirty={mudou}
        dirtyHint="Ajuste os dados e clique em Salvar"
        saveLabel="Salvar"
        saveDisabled={!mudou || !editValido}
        saving={salvarDados.isPending}
        onSubmit={() =>
          mudou &&
          editValido &&
          salvarDados.mutate({
            memberId: orig.memberId,
            json: {
              name: formEdit.name.trim(),
              email: formEdit.email.trim(),
              isActive: formEdit.isActive,
              accessGroupIds: formEdit.accessGroupIds,
              workspaceIds: formEdit.workspaceIds,
              accessChannels: formEdit.accessChannels,
              ...(formEdit.password ? { password: formEdit.password } : {}),
            },
          })
        }
      >
        <FormSection title="Informações do membro">
          <FormRow cols="1fr 1fr 1fr">
            <FieldBlock label="Nome completo" required>
              {({ id }) => (
                <Input id={id} value={formEdit.name}
                  onChange={(e) => setFormEdit((f) => ({ ...f, name: e.target.value }))} />
              )}
            </FieldBlock>
            <FieldBlock label="E-mail" required>
              {({ id }) => (
                <Input id={id} type="email" value={formEdit.email}
                  onChange={(e) => setFormEdit((f) => ({ ...f, email: e.target.value }))} />
              )}
            </FieldBlock>
            <FieldBlock label="Nova senha" hint="Em branco mantém a senha atual.">
              {({ id }) => (
                <Input id={id} type="password" value={formEdit.password} placeholder="Deixe em branco para manter"
                  onChange={(e) => setFormEdit((f) => ({ ...f, password: e.target.value }))} />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        <FormSection title="Acesso">
          <FormRow cols="1fr 1fr">
            <FieldBlock label="Grupos de acesso" required hint="Sem grupo de acesso a pessoa não consegue entrar.">
              {({ id }) => (
                <TagSelect
                  id={id}
                  value={formEdit.accessGroupIds}
                  placeholder={grupos.data?.groups.length ? 'Selecione ao menos um' : 'Nenhum grupo cadastrado'}
                  emptyLabel="Nenhum grupo"
                  options={(grupos.data?.groups ?? []).map((g) => ({ value: g.id, label: g.name }))}
                  onChange={(v) => setFormEdit((f) => ({ ...f, accessGroupIds: v }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Workspaces" required hint="Em quais workspaces da conta o membro trabalha.">
              {({ id }) => (
                <TagSelect
                  id={id}
                  value={formEdit.workspaceIds}
                  placeholder="Selecione ao menos um"
                  emptyLabel="Nenhum workspace"
                  options={(wsConta.data?.workspaces ?? []).map((w) => ({ value: w.id, label: w.name }))}
                  onChange={(v) => setFormEdit((f) => ({ ...f, workspaceIds: v }))}
                />
              )}
            </FieldBlock>
          </FormRow>
          <FieldBlock label="Situação">
            {() =>
              ehDono ? (
                <Alert tone="info">O dono do workspace permanece sempre ativo.</Alert>
              ) : (
                <Checkbox
                  label="Membro ativo (pode entrar e usar o workspace)"
                  checked={formEdit.isActive}
                  onChange={(e) => setFormEdit((f) => ({ ...f, isActive: e.target.checked }))}
                />
              )
            }
          </FieldBlock>
        </FormSection>

        {/* canais de acesso NESTE workspace (SPEC-20261008-1104-canais-de-acesso) */}
        <FormSection title="Canais de acesso">
          <Checkbox
            label="Seguir o padrão do workspace"
            checked={formEdit.accessChannels === null}
            onChange={(e) =>
              setFormEdit((f) => ({ ...f, accessChannels: e.target.checked ? null : { ...CANAIS_LIBERADOS } }))
            }
          />
          {formEdit.accessChannels !== null ? (
            <CamposDeCanais
              value={formEdit.accessChannels}
              onChange={(canais) => setFormEdit((f) => ({ ...f, accessChannels: canais }))}
            />
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  // -------------------------------- perfis diretos (padrao FormScreen) ---
  if (perfisDe !== null) {
    const alteradoPerfis =
      JSON.stringify([...perfisSelecionados].sort()) !== JSON.stringify([...perfisDe.profileIds].sort())
    return (
      <FormScreen
        title="Perfis de acesso"
        subtitle={perfisDe.name}
        onClose={() => setPerfisDe(null)}
        dirty={alteradoPerfis}
        saveLabel="Salvar"
        saveDisabled={!alteradoPerfis || salvarPerfis.isPending}
        saving={salvarPerfis.isPending}
        onSubmit={() =>
          alteradoPerfis &&
          salvarPerfis.mutate({ memberId: perfisDe.memberId, profileIds: perfisSelecionados })
        }
      >
        <FormSection title="Perfis diretos">
          {perfisDe.role === 'owner' ? (
            <Alert tone="info">O dono do workspace sempre pode tudo, independentemente de perfis.</Alert>
          ) : null}
          <FieldBlock label="Perfis do membro" hint="Além destes, a pessoa herda os perfis dos grupos em que está.">
            {() => (
              <div className="ob-stack" role="group" aria-label="Perfis do membro">
                {(perfis.data?.profiles ?? []).map((p) => (
                  <Checkbox key={p.id} label={p.name}
                    checked={perfisSelecionados.includes(p.id)}
                    onChange={(e) =>
                      setPerfisSelecionados((atual) =>
                        e.target.checked ? [...atual, p.id] : atual.filter((id) => id !== p.id),
                      )
                    } />
                ))}
              </div>
            )}
          </FieldBlock>
          {perfisDe.groupIds.length > 0 ? (
            <p className="adm-hint">
              Grupos de {perfisDe.name}: {perfisDe.groupIds.map((id) => nomeDoGrupo(id)).join(', ')}.
            </p>
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  const topo = cabecalho(
    'Membros',
    'Usuários do workspace: quem entra, com que perfis e em quais grupos. Usuário é um membro deste workspace.',
    <Button variant="primary" icon="fas fa-plus" onClick={() => { setForm(COLAB_VAZIO); setCriando(true) }}>
      Incluir
    </Button>,
  )

  if (lista.isPending) return (<>{topo}<LoadingState variant="list" label="Carregando os membros" /></>)
  if (lista.isError) {
    return (
      <>
        {topo}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar os membros"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {topo}
      <div className="adm-list">
        {members.map((row) => {
          const chips = [
            ...row.profileIds.map((id) => nomeDoPerfil(id)),
            ...row.groupIds.map((id) => `grupo ${nomeDoGrupo(id)}`),
          ]
          const legenda = [row.email, chips.length > 0 ? chips.join(', ') : 'sem perfis — vale a regra padrão do workspace']
            .filter(Boolean)
            .join(' · ')
          return (
            <div key={row.memberId} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
              <span className="adm-catrow__icon"><i className="fas fa-user" aria-hidden /></span>
              <span className="adm-catrow__main">
                <span className="adm-catrow__name">{row.name}</span>
                <span className="adm-catrow__sub">{legenda}</span>
              </span>
              {row.role === 'owner' ? (
                <Badge tone="primary" icon="fas fa-crown">Dono</Badge>
              ) : null}
              {!row.isActive ? <Badge tone="neutral">Inativo</Badge> : null}
              <button type="button" className="adm-iconbtn" aria-label={`Editar ${row.name}`} title="Editar dados"
                onClick={() => abrirEdicao(row)}>
                <i className="fas fa-pen" aria-hidden />
              </button>
              <button type="button" className="adm-iconbtn" aria-label={`Perfis de ${row.name}`} title="Perfis de acesso"
                onClick={() => { setPerfisDe(row); setPerfisSelecionados(row.profileIds) }}>
                <i className="fas fa-id-badge" aria-hidden />
              </button>
            </div>
          )
        })}
      </div>

    </>
  )
}
